import { Box, Typography } from '@mui/material';

export interface StudentServiceComparisonRow {
    name: string;
    participationRate: number;
    averageHours: number;
}

interface Props {
    rows: StudentServiceComparisonRow[];
    subtitle: string;
}

const PARTICIPATION_COLOR = '#df743d';
const HOURS_COLOR = '#2e6389';

export const StudentServiceComparisonChart = ({ rows, subtitle }: Props) => {
    const maxParticipationRate = Math.max(...rows.map(row => row.participationRate), 1);
    const maxAverageHours = Math.max(...rows.map(row => row.averageHours), 1);

    return (
        <Box sx={{ height: '100%', overflow: 'auto', px: { xs: 1, md: 2 }, pb: 2 }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mb: 1 }}>
                {subtitle}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(130px, 1fr) minmax(150px, 1.15fr) minmax(130px, 1fr)', md: '1fr minmax(260px, .95fr) 1fr' },
                    columnGap: { xs: 1, md: 2 },
                    alignItems: 'end',
                    minWidth: 520,
                    mb: 1.5,
                }}
            >
                <Typography variant="h6" align="center" sx={{ color: PARTICIPATION_COLOR, fontWeight: 700, lineHeight: 1.15 }}>
                    Participation Rates in<br />GEAR UP Services
                </Typography>
                <Box />
                <Typography variant="h6" align="center" sx={{ color: HOURS_COLOR, fontWeight: 700, lineHeight: 1.15 }}>
                    Average Hours Spent in<br />GEAR UP Services
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15, minWidth: 520 }}>
                {rows.map(row => (
                    <Box
                        key={row.name}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'minmax(130px, 1fr) minmax(150px, 1.15fr) minmax(130px, 1fr)', md: '1fr minmax(260px, .95fr) 1fr' },
                            columnGap: { xs: 1, md: 2 },
                            alignItems: 'center',
                            minHeight: 34,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: .75, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                {row.participationRate.toFixed(1)}%
                            </Typography>
                            <Box sx={{ width: '100%', height: 28, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box
                                    sx={{
                                        width: `${row.participationRate / maxParticipationRate * 100}%`,
                                        minWidth: row.participationRate > 0 ? 2 : 0,
                                        bgcolor: PARTICIPATION_COLOR,
                                    }}
                                />
                            </Box>
                        </Box>
                        <Typography variant="body2" align="center" sx={{ lineHeight: 1.15 }}>
                            {row.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, minWidth: 0 }}>
                            <Box sx={{ width: '100%', height: 28 }}>
                                <Box
                                    sx={{
                                        width: `${row.averageHours / maxAverageHours * 100}%`,
                                        minWidth: row.averageHours > 0 ? 2 : 0,
                                        height: '100%',
                                        bgcolor: HOURS_COLOR,
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                {row.averageHours.toFixed(1)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
